/**
 * The trend charts: what net worth is made of, savings rate, this month
 * against the usual, category trends, recurring charges, where the income
 * went, the spending calendar, the emergency fund, fees and gains. The same
 * rules as charts.tsx: every mark has a printed value, a label or a legend
 * with values, so colour is never the only way to read one.
 */
import { useState } from 'react'
import { GROUP_LABEL, type Amount, type Group, type Holding, type NetWorthPoint } from './lib/analysis'
import type { CategorySeries, RecurringMonth, SavingsRate, VersusUsual } from './lib/trends'
import { monthLabel, niceMax, Tip, useHover, useWidth } from './charts'

const whole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const compact = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 })
const pct = (n: number) => `${Math.round(n * 100)}%`
/** Whole dollars under $1,000, compact above. */
const money = (n: number) => (Math.abs(n) < 1000 ? whole.format(n) : compact.format(n))
const signedPct = (n: number) => `${n < 0 ? '−' : ''}${Math.round(Math.abs(n) * 100)}%`

/**
 * Asset groups bottom to top, each with a fixed colour (colour follows the
 * group, never its rank). This order was checked with the dataviz validator in
 * both themes: adjacent CVD ΔE ≥ 8.4. Debt sits below zero, so position
 * already says which band it is.
 */
const BANDS: { group: Group; color: string }[] = [
  { group: 'cash', color: 'var(--series-1)' },
  { group: 'taxable', color: 'var(--series-2)' },
  { group: 'retirement', color: 'var(--series-3)' },
  { group: 'hsa', color: 'var(--series-4)' },
  { group: 'education', color: 'var(--series-5)' },
  { group: 'property', color: 'var(--series-7)' },
  { group: 'other', color: 'var(--series-other)' },
]
const DEBT_COLOR = 'var(--series-8)'

/** Net worth as stacked bands per group, debt below zero, with the net line over them. */
export function NetWorthBands({ points }: { points: NetWorthPoint[] }) {
  const [hover, setHover] = useHover(points.length)
  const [ref, W] = useWidth()
  if (points.length < 2) return <p className="muted">The bands start once two months of statements from every account are in.</p>
  const bands = BANDS.filter((b) => points.some((p) => (p.by_group?.[b.group] ?? 0) > 0))
  const hasDebt = points.some((p) => (p.by_group?.debt ?? 0) > 0)
  const H = 260
  const pad = { l: 64, r: 16, t: 12, b: 28 }
  const top = niceMax(Math.max(...points.map((p) => bands.reduce((s, b) => s + Math.max(0, p.by_group?.[b.group] ?? 0), 0)), 1))
  const bottom = hasDebt ? niceMax(Math.max(...points.map((p) => p.by_group?.debt ?? 0))) : 0
  const x = (i: number) => pad.l + (i * (W - pad.l - pad.r)) / (points.length - 1)
  const y = (v: number) => pad.t + ((top - v) / (top + bottom)) * (H - pad.t - pad.b)
  // Cumulative tops per band, bottom up.
  const stacks = points.map((p) => {
    let run = 0
    return bands.map((b) => (run += Math.max(0, p.by_group?.[b.group] ?? 0)))
  })
  const bandPath = (k: number) => {
    const upper = points.map((_, i) => `${i ? 'L' : 'M'}${x(i)},${y(stacks[i][k])}`).join(' ')
    const lower = points.map((_, i) => `L${x(points.length - 1 - i)},${y(k ? stacks[points.length - 1 - i][k - 1] : 0)}`).join(' ')
    return `${upper} ${lower} Z`
  }
  const debtPath = () => {
    const lower = points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(-(p.by_group?.debt ?? 0))}`).join(' ')
    return `${lower} L${x(points.length - 1)},${y(0)} L${x(0)},${y(0)} Z`
  }
  const net = points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.net)}`).join(' ')
  const last = points.at(-1)!
  // The debt tick only when there is room for it below the $0 label.
  const ticks = [top, top / 2, 0, ...(hasDebt && y(-bottom) - y(0) >= 14 ? [-bottom] : [])]
  const half = (W - pad.l - pad.r) / (points.length - 1) / 2
  return (
    <figure className="chart hoverable" ref={ref}>
      <div className="legend">
        {[...bands].reverse().map((b) => (
          <span key={b.group}>
            <i className="swatch" style={{ background: b.color }} /> {GROUP_LABEL[b.group]} <span className="num">{compact.format(last.by_group?.[b.group] ?? 0)}</span>
          </span>
        ))}
        {hasDebt && (
          <span>
            <i className="swatch" style={{ background: DEBT_COLOR }} /> Owed (below $0) <span className="num">−{compact.format(last.by_group?.debt ?? 0)}</span>
          </span>
        )}
        <span><i className="swatch line-swatch" /> Net worth <span className="num">{compact.format(last.net)}</span></span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Net worth by type of account, by month">
        {ticks.map((t) => (
          <g key={t}>
            <line className={t === 0 ? 'axis' : 'grid'} x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} />
            <text className="tick" x={pad.l - 8} y={y(t) + 4} textAnchor="end">{t < 0 ? '−' : ''}{compact.format(Math.abs(t))}</text>
          </g>
        ))}
        {bands.map((b, k) => <path key={b.group} className="band" d={bandPath(k)} style={{ fill: b.color }} />)}
        {hasDebt && <path className="band" d={debtPath()} style={{ fill: DEBT_COLOR }} />}
        <path className="net-line" d={net} />
        {hover !== undefined && <line className="crosshair" x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} />}
        {points.map((p, i) => (
          <text key={p.month} className="tick" x={x(i)} y={H - 8} textAnchor="middle">{monthLabel(p.month, i, points.length)}</text>
        ))}
        {points.map((p, i) => (
          <rect
            key={p.month}
            className="hit"
            x={Math.max(pad.l, x(i) - half)}
            width={Math.min(x(i) + half, W - pad.r) - Math.max(pad.l, x(i) - half)}
            y={pad.t}
            height={H - pad.t - pad.b}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(undefined)}
            onClick={() => setHover(hover === i ? undefined : i)}
          />
        ))}
      </svg>
      {hover !== undefined && (
        <Tip at={x(hover) / W}>
          <b className="num">{points[hover].month}</b>
          <table>
            <tbody>
              {[...bands].reverse().map((b) => (
                <tr key={b.group} className="sub">
                  <td><i className="swatch" style={{ background: b.color }} />{GROUP_LABEL[b.group]}</td>
                  <td className="num r">{whole.format(points[hover].by_group?.[b.group] ?? 0)}</td>
                </tr>
              ))}
              {hasDebt && (
                <tr className="sub"><td><i className="swatch" style={{ background: DEBT_COLOR }} />Owed</td><td className="num r">−{whole.format(points[hover].by_group?.debt ?? 0)}</td></tr>
              )}
              <tr><td>Net worth</td><td className="num r">{whole.format(points[hover].net)}</td></tr>
            </tbody>
          </table>
        </Tip>
      )}
    </figure>
  )
}

/** Savings rate per month as bars from zero, with the rate over the whole window as a labelled line. */
export function SavingsRateChart({ months, overall }: { months: SavingsRate[]; overall: number | null }) {
  const [hover, setHover] = useHover(months.length)
  const [ref, W] = useWidth()
  if (months.every((m) => m.rate === null)) return <p className="muted">No income in the statements yet, so there is no savings rate.</p>
  const rates = months.map((m) => m.rate).filter((r): r is number => r !== null)
  // Clamp the scale to ±100% so one month with a large shortfall doesn't flatten the rest; its label keeps the true value.
  const top = Math.min(1, Math.max(0.25, ...rates, overall ?? 0))
  const bottom = Math.min(1, Math.max(0, ...rates.map((r) => -r)))
  const H = 200
  const pad = { l: 48, r: 8, t: 20, b: 28 }
  const plotH = H - pad.t - pad.b
  const y = (r: number) => pad.t + ((top - Math.max(-bottom, Math.min(top, r))) / (top + bottom || 1)) * plotH
  const zero = y(0)
  const slot = (W - pad.l - pad.r) / months.length
  const bar = Math.min(40, slot * 0.5)
  return (
    <figure className="chart hoverable" ref={ref}>
      <figcaption className="muted">
        {overall === null ? '' : `${signedPct(overall)} of income kept over these ${months.length} months (the line). `}
        Bars below the line are months you spent more than came in.
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Savings rate by month">
        <text className="tick" x={pad.l - 8} y={y(top) + 4} textAnchor="end">{pct(top)}</text>
        <text className="tick" x={pad.l - 8} y={zero + 4} textAnchor="end">0%</text>
        {bottom > 0 && <text className="tick" x={pad.l - 8} y={y(-bottom) + 4} textAnchor="end">−{pct(bottom)}</text>}
        <line className="axis" x1={pad.l} x2={W - pad.r} y1={zero} y2={zero} />
        {months.map((m, i) => {
          const cx = pad.l + slot * i + slot / 2
          const r = m.rate
          return (
            <g key={m.month}>
              {hover === i && <rect className="column" x={pad.l + slot * i + 2} width={slot - 4} y={pad.t - 14} height={plotH + 14} rx={4} />}
              {r !== null && (
                <rect
                  className="bar"
                  x={cx - bar / 2}
                  y={r >= 0 ? y(r) : zero + 1}
                  width={bar}
                  height={Math.max(Math.abs(y(r) - zero), 0.5)}
                  rx={2}
                  style={{ fill: r >= 0 ? 'var(--series-1)' : 'var(--series-2)' }}
                />
              )}
              {/* Too narrow for every figure, the labelled months and any month below 0% carry them. */}
              {(slot >= 44 || (r !== null && r < 0) || monthLabel(m.month, i, months.length)) && (
                <text className={r !== null && r < 0 ? 'value neg' : 'value'} x={cx} y={r === null ? zero - 5 : r >= 0 ? y(r) - 5 : y(r) + 13} textAnchor="middle">
                  {r === null ? '—' : signedPct(r)}
                </text>
              )}
              <text className="tick" x={cx} y={H - 8} textAnchor="middle">{monthLabel(m.month, i, months.length)}</text>
              <rect className="hit" x={pad.l + slot * i} width={slot} y={0} height={H} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(undefined)} />
            </g>
          )
        })}
        {overall !== null && (
          <line className="ref-line" x1={pad.l} x2={W - pad.r} y1={y(overall)} y2={y(overall)} />
        )}
      </svg>
      {hover !== undefined && (
        <Tip at={(pad.l + slot * hover + slot / 2) / W}>
          <b className="num">{months[hover].month}</b>
          <p className="tip-hint">{months[hover].rate === null ? 'No income this month.' : `${signedPct(months[hover].rate!)} of income kept`}</p>
        </Tip>
      )}
    </figure>
  )
}

/**
 * One row per category: the range of its other months as a band, the usual
 * (median) month as a tick, and the comparison month as a dot, with the
 * difference printed in words.
 */
export function VersusUsualChart({ rows, month, onSelect }: { rows: VersusUsual[]; month: string; onSelect?: (category: string) => void }) {
  const [ref, W] = useWidth()
  if (rows.length === 0) return <p className="muted">This needs two or more months of spending.</p>
  const rowH = 30
  const pad = { l: 150, r: 190, t: 8 }
  const H = pad.t + rows.length * rowH + 4
  const max = niceMax(Math.max(...rows.map((r) => Math.max(r.high, r.latest)), 1))
  const x = (v: number) => pad.l + (Math.max(0, v) / max) * (W - pad.l - pad.r)
  const verdict = (r: VersusUsual) => {
    if (r.usual === 0) return r.latest > 0 ? 'none usually' : 'none'
    const d = (r.latest - r.usual) / r.usual
    if (Math.abs(d) < 0.1) return '≈ usual'
    return `${d > 0 ? '▲' : '▼'} ${pct(Math.abs(d))} ${d > 0 ? 'above' : 'below'}`
  }
  return (
    <figure className="chart" ref={ref}>
      <div className="legend">
        <span><i className="swatch range-swatch" /> Range of the other months</span>
        <span><i className="swatch tick-swatch" /> Usual month (median)</span>
        <span><i className="swatch dot-swatch" /> {month}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Spending by category in ${month} against the usual month`}>
        {rows.map((r, i) => {
          const cy = pad.t + i * rowH + rowH / 2
          const above = r.usual > 0 && r.latest > r.usual * 1.1
          return (
            <g key={r.category} className={onSelect ? 'clickable' : undefined} onClick={() => onSelect?.(r.category)}>
              <title>{`${r.category}: ${whole.format(r.latest)} in ${month}; usually ${whole.format(r.usual)} (${whole.format(r.low)}–${whole.format(r.high)})`}</title>
              <rect className="hit" x={0} y={cy - rowH / 2} width={W} height={rowH} />
              <text className="row-label" x={0} y={cy + 4}>{r.category}</text>
              <line className="grid" x1={pad.l} x2={W - pad.r} y1={cy} y2={cy} />
              <rect className="range" x={x(r.low)} y={cy - 5} width={Math.max(x(r.high) - x(r.low), 2)} height={10} rx={2} />
              <line className="usual" x1={x(r.usual)} x2={x(r.usual)} y1={cy - 9} y2={cy + 9} />
              <circle className="latest" cx={x(r.latest)} cy={cy} r={6} />
              <text className="value" x={W - pad.r + 12} y={cy + 4}>{whole.format(r.latest)}</text>
              <text className={above ? 'value neg' : 'value'} x={W - pad.r + 84} y={cy + 4}>{verdict(r)}</text>
            </g>
          )
        })}
      </svg>
    </figure>
  )
}

/** Small multiples: one line per top category, all on the same scale so their sizes compare. */
export function CategoryTrends({ series, months, onSelect }: { series: CategorySeries[]; months: string[]; onSelect?: (category: string) => void }) {
  if (series.length === 0 || months.length < 2) return <p className="muted">This needs two or more months of spending.</p>
  const max = niceMax(Math.max(...series.flatMap((s) => s.values), 1))
  const w = 200
  const h = 56
  const x = (i: number) => (i * w) / (months.length - 1)
  const y = (v: number) => h - (Math.max(0, v) / max) * h
  return (
    <>
      <ul className="multiples">
        {series.map((s) => {
          const line = s.values.map((v, i) => `${i ? 'L' : 'M'}${x(i)},${y(v)}`).join(' ')
          const last = s.values.at(-1)!
          const avg = s.total / months.length
          return (
            <li key={s.category} className="card">
              <div className="row">
                {onSelect ? <button className="link" onClick={() => onSelect(s.category)}>{s.category}</button> : <span>{s.category}</span>}
                <span className="num small-figure">{whole.format(avg)}/mo</span>
              </div>
              <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label={`${s.category} spending by month`}>
                <title>{months.map((m, i) => `${m}: ${whole.format(s.values[i])}`).join('\n')}</title>
                <path className="spark-area" d={`${line} L${w},${h} L0,${h} Z`} />
                <path className="spark-line" d={line} />
              </svg>
              <div className="row label">
                <span>{months[0]}</span>
                <span>{months.at(-1)} · <span className="num">{whole.format(last)}</span></span>
              </div>
            </li>
          )
        })}
      </ul>
      <p className="muted small">Every panel shares one scale, $0 to {compact.format(max)} a month, so a taller line is more money. Hover a panel for its months.</p>
    </>
  )
}

/** Recurring charges per month, with a marker under each month that a new one began. */
export function RecurringChart({ months }: { months: RecurringMonth[] }) {
  const [hover, setHover] = useHover(months.length)
  const [ref, W] = useWidth()
  if (months.every((m) => m.total === 0)) return <p className="muted">No recurring charges found yet.</p>
  const H = 190
  const pad = { l: 56, r: 8, t: 20, b: 44 }
  const top = niceMax(Math.max(...months.map((m) => m.total), 1))
  const plotH = H - pad.t - pad.b
  const y = (v: number) => pad.t + ((top - v) / top) * plotH
  const slot = (W - pad.l - pad.r) / months.length
  const bar = Math.min(40, slot * 0.5)
  return (
    <figure className="chart hoverable" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Recurring charges by month">
        <text className="tick" x={pad.l - 8} y={pad.t + 4} textAnchor="end">{compact.format(top)}</text>
        <text className="tick" x={pad.l - 8} y={y(0) + 4} textAnchor="end">$0</text>
        <line className="axis" x1={pad.l} x2={W - pad.r} y1={y(0)} y2={y(0)} />
        {months.map((m, i) => {
          const cx = pad.l + slot * i + slot / 2
          return (
            <g key={m.month}>
              {hover === i && <rect className="column" x={pad.l + slot * i + 2} width={slot - 4} y={pad.t - 14} height={plotH + 14} rx={4} />}
              <rect className="bar" x={cx - bar / 2} y={y(m.total)} width={bar} height={Math.max(y(0) - y(m.total), 0.5)} rx={2} style={{ fill: 'var(--series-2)' }} />
              {m.total > 0 && (slot >= 44 || monthLabel(m.month, i, months.length)) && (
                <text className="value" x={cx} y={y(m.total) - 5} textAnchor="middle">{compact.format(m.total)}</text>
              )}
              <text className="tick" x={cx} y={H - 26} textAnchor="middle">{monthLabel(m.month, i, months.length)}</text>
              {m.started.length > 0 && <text className="value neg" x={cx} y={H - 8} textAnchor="middle">+{m.started.length} new</text>}
              <rect className="hit" x={pad.l + slot * i} width={slot} y={0} height={H} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(undefined)} />
            </g>
          )
        })}
      </svg>
      {hover !== undefined && (
        <Tip at={(pad.l + slot * hover + slot / 2) / W}>
          <b className="num">{months[hover].month}</b> <span className="num">{whole.format(months[hover].total)}</span>
          <table>
            <tbody>
              {months[hover].charges.slice(0, 10).map((c) => (
                <tr key={c.merchant} className="sub">
                  <td>{months[hover].started.includes(c.merchant) ? '+ new · ' : ''}{c.merchant}</td>
                  <td className="num r">{whole.format(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {months[hover].charges.length > 10 && <p className="tip-hint">and {months[hover].charges.length - 10} more</p>}
        </Tip>
      )}
      <p className="label">Bottom row: recurring charges first seen that month</p>
    </figure>
  )
}

/**
 * Where the income went: income on the left, split into spending categories
 * and what was kept on the right. Every node prints its name and amount, and
 * kept money is a different colour from spending as well as listed last.
 */
export function IncomeFlow({ income, categories, top = 7 }: { income: number; categories: Amount[]; top?: number }) {
  const [ref, W] = useWidth()
  const spent = categories.reduce((s, c) => s + c.total, 0)
  if (income <= 0 || spent <= 0) return <p className="muted">This needs both income and spending in the statements.</p>
  const shown = categories.slice(0, top)
  const rest = categories.slice(top).reduce((s, c) => s + c.total, 0)
  const kept = income - spent
  const right: { name: string; total: number; kept?: boolean }[] = [
    ...shown.map((c) => ({ name: c.label, total: c.total })),
    ...(rest > 0 ? [{ name: `Other (${categories.length - top})`, total: rest }] : []),
    ...(kept > 0 ? [{ name: 'Kept', total: kept, kept: true }] : []),
  ]
  const left: { name: string; total: number; kept?: boolean }[] = [
    { name: 'Income', total: income },
    ...(kept < 0 ? [{ name: 'From savings', total: -kept, kept: true }] : []),
  ]
  const sum = Math.max(income, spent)
  const gap = 6
  const H = Math.max(240, right.length * 30)
  const plotH = H - gap * (right.length - 1)
  const nodeW = 14
  const lx = 0
  const rx = W - 240
  const scale = plotH / sum
  const share = (n: number) => pct(n / income)
  // Right nodes stacked with gaps; ribbons leave the left side in the same order without gaps.
  let ry = 0
  let ly = 0
  const ribbons = right.map((r) => {
    const h = r.total * scale
    const out = { ...r, y0: ly, y1: ry, h }
    ly += h
    ry += h + gap
    return out
  })
  // Labels sit at their node's centre, pushed down where small nodes would make them overlap.
  let floor = -Infinity
  const labelY = ribbons.map((r) => (floor = Math.max(r.y1 + Math.max(r.h, 1) / 2 + 4, floor + 17)))
  let lyNode = 0
  const leftNodes = left.map((n) => {
    const h = n.total * scale
    const out = { ...n, y: lyNode, h }
    lyNode += h
    return out
  })
  const curve = (y0: number, y1: number, h: number) => {
    const a = lx + nodeW
    const b = rx
    const m = (a + b) / 2
    return `M${a},${y0} C${m},${y0} ${m},${y1} ${b},${y1} L${b},${y1 + h} C${m},${y1 + h} ${m},${y0 + h} ${a},${y0 + h} Z`
  }
  return (
    <figure className="chart" ref={ref}>
      <svg viewBox={`0 0 ${W} ${Math.max(H, labelY.at(-1)! + 6)}`} role="img" aria-label="Where the income went">
        {ribbons.map((r) => (
          <path key={r.name} className="ribbon" d={curve(r.y0, r.y1, r.h)} style={{ fill: r.kept ? 'var(--series-1)' : 'var(--series-2)' }}>
            <title>{`${r.name}: ${whole.format(r.total)}, ${share(r.total)} of income`}</title>
          </path>
        ))}
        {leftNodes.map((n) => (
          <g key={n.name}>
            <rect x={lx} y={n.y} width={nodeW} height={Math.max(n.h, 1)} rx={2} style={{ fill: n.kept ? 'var(--series-1)' : 'var(--c-ink-2)' }} />
            <text className="row-label" x={lx + nodeW + 8} y={n.y + 16}>{n.name}</text>
            <text className="value" x={lx + nodeW + 8} y={n.y + 30}>{whole.format(n.total)}</text>
          </g>
        ))}
        {ribbons.map((r, i) => (
          <g key={r.name}>
            <rect x={rx} y={r.y1} width={nodeW} height={Math.max(r.h, 1)} rx={2} style={{ fill: r.kept ? 'var(--series-1)' : 'var(--series-2)' }} />
            <text className={r.kept ? 'row-label strong' : 'row-label'} x={rx + nodeW + 8} y={labelY[i]}>
              {r.kept ? '▲ ' : ''}{r.name} <tspan className="value">{whole.format(r.total)} · {share(r.total)}</tspan>
            </text>
          </g>
        ))}
      </svg>
    </figure>
  )
}

/**
 * A year of days as a grid, weeks across and weekdays down, shaded lighter to
 * darker by spending in five steps of one hue. The legend prints each step's
 * range, and days with nothing spent are the lightest.
 */
export function SpendingCalendar({ days, start, end }: { days: Map<string, number>; start: string; end: string }) {
  const [ref, W] = useWidth()
  const [hover, setHover] = useState<{ date: string; amount: number; at: number; top: number }>()
  const last = new Date(`${end}T00:00:00Z`)
  // Start on the Monday of the week holding `start`.
  const startDay = new Date(`${start}T00:00:00Z`)
  startDay.setUTCDate(startDay.getUTCDate() - ((startDay.getUTCDay() + 6) % 7))
  const cells: { date: string; week: number; dow: number; amount: number }[] = []
  for (let d = new Date(startDay), i = 0; d <= last; d.setUTCDate(d.getUTCDate() + 1), i++) {
    const date = d.toISOString().slice(0, 10)
    cells.push({ date, week: Math.floor(i / 7), dow: i % 7, amount: days.get(date) ?? 0 })
  }
  const spent = cells.map((c) => c.amount).filter((a) => a > 0).sort((a, b) => a - b)
  if (spent.length === 0) return <p className="muted">No spending in this range.</p>
  // Quintile edges of the days with spending, so each step holds about a fifth of them.
  const edges = [0.2, 0.4, 0.6, 0.8].map((q) => spent[Math.min(spent.length - 1, Math.floor(q * spent.length))])
  const step = (a: number) => (a <= 0 ? 0 : 1 + edges.filter((e) => a > e).length)
  const weeks = cells.at(-1)!.week + 1
  const padL = 36
  const padT = 18
  const size = Math.max(8, Math.min(24, Math.floor((W - padL) / weeks) - 2))
  const pitch = size + 2
  const H = padT + 7 * pitch
  const DOW = ['Mon', '', 'Wed', '', 'Fri', '', '']
  const monthStarts = cells.filter((c) => c.date.endsWith('-01'))
  const bounds = [0, ...edges, spent.at(-1)!]
  return (
    <figure className="chart hoverable" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Spending per day, ${start} to ${end}`}>
        {DOW.map((d, i) => d && <text key={i} className="tick" x={0} y={padT + i * pitch + size - 1}>{d}</text>)}
        {monthStarts
          .filter((_, i, all) => pitch * 4.35 >= 60 || (all.length - 1 - i) % 2 === 0)
          .map((c) => <text key={c.date} className="tick" x={padL + c.week * pitch} y={10}>{c.date.slice(0, 7)}</text>)}
        {cells.map((c) => (
          <rect
            key={c.date}
            className={`day s${step(c.amount)}`}
            x={padL + c.week * pitch}
            y={padT + c.dow * pitch}
            width={size}
            height={size}
            rx={2}
            onMouseEnter={() => setHover({ date: c.date, amount: c.amount, at: (padL + c.week * pitch) / W, top: padT + c.dow * pitch + pitch })}
            onMouseLeave={() => setHover(undefined)}
          >
            <title>{`${c.date}: ${whole.format(c.amount)}`}</title>
          </rect>
        ))}
      </svg>
      {hover && (
        <Tip at={hover.at} top={hover.top}>
          <b className="num">{hover.date}</b> <span className="num">{whole.format(hover.amount)}</span>
        </Tip>
      )}
      <div className="legend calendar-legend">
        <span><i className="swatch day s0" /> nothing</span>
        {[1, 2, 3, 4, 5].map((s) => (
          <span key={s}>
            <i className={`swatch day s${s}`} /> <span className="num">{money(bounds[s - 1])}–{money(bounds[s])}</span>
          </span>
        ))}
      </div>
    </figure>
  )
}

/** Months of spending held in cash against the target, on one scale, with the target marked and labelled. */
export function EmergencyFund({ months, target, monthly }: { months: number | null; target: number | null; monthly: number }) {
  if (months === null) return <p className="muted">This needs spending data to know what a month costs.</p>
  const marks = target !== null ? [{ at: target, text: `target ${target} mo` }] : [{ at: 3, text: '3 mo' }, { at: 6, text: '6 mo' }]
  const scale = Math.max(months, ...marks.map((m) => m.at)) * 1.15
  const at = (v: number) => `${(v / scale) * 100}%`
  const short = target !== null && months < target ? target - months : 0
  return (
    <figure className="chart">
      <div className="gauge">
        <span className="gauge-fill" style={{ width: at(months) }} />
        {marks.map((m) => (
          <span key={m.text} className="gauge-mark" style={{ left: at(m.at) }}>
            <span className="label">{m.text}</span>
          </span>
        ))}
      </div>
      <p className="figure-line">
        <b className="num">{months.toFixed(1)}</b> months of spending in cash, at {whole.format(monthly)} a month.{' '}
        {target === null
          ? 'No target in your Profile; 3 and 6 months are marked for reference.'
          : short > 0
            ? `▼ ${short.toFixed(1)} months (${whole.format(short * monthly)}) short of your ${target}-month target.`
            : `▲ At or above your ${target}-month target.`}
      </p>
    </figure>
  )
}

/** Gains to the right of the centre line, losses to the left, each printed with ▲/▼ and its percentage of cost. */
export function GainsChart({ holdings }: { holdings: Holding[] }) {
  const rows = holdings.filter((h) => h.unrealized !== null && h.value > 0)
  if (rows.length === 0) return <p className="muted">The statements don't show cost basis for these holdings.</p>
  const max = Math.max(...rows.map((h) => Math.abs(h.unrealized!)), 1)
  return (
    <ul className="diverge">
      {rows.map((h) => {
        const g = h.unrealized!
        const basis = h.value - g
        const w = `${(Math.abs(g) / max) * 100}%`
        return (
          <li key={h.symbol || h.name} title={`${h.symbol} ${h.name}: value ${whole.format(h.value)}, cost ${whole.format(basis)}`}>
            <span className="bar-label"><span className="num">{h.symbol || h.name}</span></span>
            <span className="half loss">{g < 0 && <span className="bar-fill" style={{ width: w, background: 'var(--series-2)' }} />}</span>
            <span className="half gain">{g >= 0 && <span className="bar-fill" style={{ width: w, background: 'var(--series-1)' }} />}</span>
            <span className={g < 0 ? 'num r strong' : 'num r'}>
              {g >= 0 ? '▲' : '▼'} {whole.format(Math.abs(g))}{basis > 0 ? ` (${g >= 0 ? '+' : '−'}${pct(Math.abs(g) / basis)})` : ''}
            </span>
          </li>
        )
      })}
    </ul>
  )
}
